import { ReactElement } from 'react';

import {
  FormControl,
  FormHelperText,
  FormLabel,
  TextareaAutosizeProps as MuiTextareaAutosizeProps,
} from '@mui/material';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form';
import { CustomTextareaAutosize } from '../custom/CustomTextareaAutosize';

export type TextareaAutosizeProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<MuiTextareaAutosizeProps, 'inputRef'> & {
  ControllerProps: Omit<ControllerProps<TFieldValues, TName>, 'render'>;
  label?: string;
  helperText?: string;
};

export const Textarea = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ControllerProps,
  label,
  helperText,
  ...props
}: TextareaAutosizeProps<TFieldValues, TName>): ReactElement => {
  return (
    <Controller
      {...ControllerProps}
      render={({
        field: { ref, onChange, ...field },
        fieldState: { error },
      }) => (
        <FormControl error={Boolean(error)} fullWidth>
          {label && <FormLabel>{label}</FormLabel>}{' '}
          <CustomTextareaAutosize
            {...props}
            {...field}
            ref={ref}
            onChange={(event) => {
              onChange(event.target.value);
              props.onChange && props.onChange(event);
            }}
          />
          {(error || helperText) && (
            <FormHelperText>{error?.message || helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};
